// src/utils/responsive.ts
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery as useMediaQueryHook } from 'usehooks-ts';

/**
 * Custom hook for responsive breakpoints using Material UI
 * Now includes xxl and ultra-wide screen support
 */
export const useResponsive = () => {
  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 640px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg')); // 640px - 1023px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1024px
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl')); // >= 1280px
  const isXLarge = useMediaQuery(theme.breakpoints.up('xxl')); // >= 1536px
  const isUltraWide = useMediaQuery('(min-width: 1920px)'); // >= 1920px
  
  // Specific breakpoint checks
  const isSmallMobile = useMediaQuery('(max-width: 375px)');
  const isLargeMobile = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isSmallTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  return {
    // Primary breakpoints
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isXLarge,
    isUltraWide,
    
    // Specific breakpoints
    isSmallMobile,
    isLargeMobile,
    isSmallTablet,
    
    // Helper function to get current breakpoint
    breakpoint: isMobile 
      ? 'mobile' 
      : isTablet 
      ? 'tablet' 
      : isXLarge
      ? 'xxl'
      : isLargeDesktop
      ? 'xl'
      : 'desktop',
      
    // Screen width category
    screenSize: (isSmallMobile
      ? 'xs'
      : isMobile
      ? 'sm'
      : isTablet
      ? 'md'
      : isDesktop && !isLargeDesktop
      ? 'lg'
      : isLargeDesktop && !isXLarge
      ? 'xl'
      : isXLarge && !isUltraWide
      ? 'xxl'
      : 'ultrawide') as 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'ultrawide',
  };
};

/**
 * Custom hook using usehooks-ts for specific media queries
 */
export const useCustomMediaQuery = (query: string) => {
  return useMediaQueryHook(query);
};

/**
 * Enhanced responsive value helper - returns different values based on breakpoint
 * Now supports xxl and ultrawide
 */
export const getResponsiveValue = <T,>(
  values: {
    mobile?: T;
    tablet?: T;
    desktop?: T;
    xl?: T;
    xxl?: T;
    ultrawide?: T;
  },
  breakpoint: 'mobile' | 'tablet' | 'desktop' | 'xl' | 'xxl' | 'ultrawide'
): T => {
  // Return exact match if available
  if (values[breakpoint] !== undefined) {
    return values[breakpoint]!;
  }
  
  // Fallback chain for larger screens
  if (breakpoint === 'ultrawide') {
    return values.xxl ?? values.xl ?? values.desktop ?? values.tablet ?? values.mobile!;
  }
  if (breakpoint === 'xxl') {
    return values.xl ?? values.desktop ?? values.tablet ?? values.mobile!;
  }
  if (breakpoint === 'xl') {
    return values.desktop ?? values.tablet ?? values.mobile!;
  }
  if (breakpoint === 'desktop') {
    return values.tablet ?? values.mobile!;
  }
  if (breakpoint === 'tablet') {
    return values.mobile!;
  }
  
  return values.mobile!;
};

/**
 * Improved fluid spacing calculator using clamp
 * Better formula for consistent scaling
 */
export const fluidSpacing = (
  minSize: number,
  maxSize: number,
  minViewport: number = 320,
  maxViewport: number = 1920 // Increased for ultra-wide support
): string => {
  // Calculate the slope and intercept for linear interpolation
  const slope = (maxSize - minSize) / (maxViewport - minViewport);
  const yIntercept = minSize - (slope * minViewport);
  
  // Convert to viewport units percentage
  const vwValue = slope * 100;
  
  return `clamp(${minSize}px, ${yIntercept.toFixed(2)}px + ${vwValue.toFixed(2)}vw, ${maxSize}px)`;
};

/**
 * Enhanced responsive font size using clamp
 * More intuitive parameters
 */
export const responsiveFontSize = (
  minSize: number,
  maxSize: number,
  options: {
    minViewport?: number;
    maxViewport?: number;
    capAtReadableSize?: boolean; // Cap body text for readability
  } = {}
): string => {
  const {
    minViewport = 320,
    maxViewport = 1920,
    capAtReadableSize = false,
  } = options;
  
  // Cap body text at 20px for better readability on large screens
  const effectiveMaxSize = capAtReadableSize && maxSize > 20 ? 20 : maxSize;
  
  return fluidSpacing(minSize, effectiveMaxSize, minViewport, maxViewport);
};

/**
 * Container queries helper
 * Using standard @container syntax
 */
export const containerQuery = {
  mobile: '@container (max-width: 640px)',
  tablet: '@container (min-width: 641px) and (max-width: 1023px)',
  desktop: '@container (min-width: 1024px)',
  largeDesktop: '@container (min-width: 1280px)',
  xlarge: '@container (min-width: 1536px)',
  ultrawide: '@container (min-width: 1920px)',
};

/**
 * Aspect ratio helper for responsive containers
 */
export const aspectRatio = (width: number, height: number): string => {
  return `${width} / ${height}`;
};

/**
 * Enhanced responsive padding generator with full breakpoint support
 */
export const responsivePadding = {
  container: {
    mobile: '1.5rem', // 24px
    tablet: '2rem', // 32px
    desktop: '2.5rem', // 40px
    xl: '3rem', // 48px
    xxl: '3.5rem', // 56px
    ultrawide: '4rem', // 64px
    fluid: 'clamp(1.5rem, 2.5vw, 4rem)',
  },
  section: {
    mobile: '3rem 1.5rem', // 48px 24px
    tablet: '4rem 2rem', // 64px 32px
    desktop: '5rem 2.5rem', // 80px 40px
    xl: '6rem 3rem', // 96px 48px
    xxl: '7rem 3.5rem', // 112px 56px
    ultrawide: '8rem 4rem', // 128px 64px
    fluidVertical: 'clamp(3rem, 5vw, 8rem)',
    fluidHorizontal: 'clamp(1.5rem, 2.5vw, 4rem)',
  },
  card: {
    mobile: '1rem', // 16px
    tablet: '1.25rem', // 20px
    desktop: '1.5rem', // 24px
    xl: '2rem', // 32px
    xxl: '2.25rem', // 36px
    ultrawide: '2.5rem', // 40px
    fluid: 'clamp(1rem, 2vw, 2.5rem)',
  },
};

/**
 * Grid spacing helper with enhanced breakpoint support
 */
export const gridSpacing = {
  small: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6, xxl: 7 },
  medium: { xs: 3, sm: 4, md: 5, lg: 6, xl: 7, xxl: 8 },
  large: { xs: 4, sm: 5, md: 6, lg: 7, xl: 8, xxl: 10 },
  xlarge: { xs: 5, sm: 6, md: 7, lg: 8, xl: 10, xxl: 12 },
};

/**
 * Responsive border radius with larger screen support
 */
export const responsiveBorderRadius = {
  small: 'clamp(0.5rem, 1vw, 1rem)', // 8px -> 16px
  medium: 'clamp(1rem, 2vw, 2rem)', // 16px -> 32px
  large: 'clamp(2rem, 3vw, 4.125rem)', // 32px -> 66px (search form)
  xlarge: 'clamp(2.5rem, 4vw, 5rem)', // 40px -> 80px (hero sections)
  round: '50%',
};

/**
 * Max width constraints for content on large screens
 * Prevents content from becoming too wide and hard to read
 */
export const maxWidthConstraints = {
  text: '65ch', // Optimal reading width (65 characters)
  content: '924px', // General content max-width
  form: '640px', // Form max-width
  container: '1440px', // General container
  wide: '1600px', // Wide layouts
  full: '1920px', // Full width cap for ultra-wide
  
  // Utility to create centered container
  centered: (maxWidth: string = '1440px') => ({
    maxWidth,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
  }),
};

/**
 * Viewport-based responsive utilities
 */
export const viewportUtils = {
  // Full width with max constraint
  fullWidthCapped: {
    width: '100%',
    maxWidth: '1920px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  
  // Responsive container with fluid padding
  responsiveContainer: {
    width: '100%',
    maxWidth: '1440px',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: 'clamp(1.5rem, 2.5vw, 4rem)',
    paddingRight: 'clamp(1.5rem, 2.5vw, 4rem)',
  },
  
  // Wide container for dashboards/complex layouts
  wideContainer: {
    width: '100%',
    maxWidth: '1600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: 'clamp(2rem, 3vw, 5rem)',
    paddingRight: 'clamp(2rem, 3vw, 5rem)',
  },
  
  // Content container optimized for reading
  contentContainer: {
    width: '100%',
    maxWidth: '924px',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: 'clamp(1.5rem, 2vw, 3rem)',
    paddingRight: 'clamp(1.5rem, 2vw, 3rem)',
  },
};

/**
 * Column layout helpers for large screens
 */
export const columnLayouts = {
  // Auto-fill grid that caps column count on large screens
  autoGrid: (minColWidth: string = '300px', maxCols: number = 4) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(min(${minColWidth}, 100%), 1fr))`,
    gap: 'clamp(1rem, 2vw, 2rem)',
    
    // Cap columns on ultra-wide screens
    '@media (min-width: 1920px)': {
      gridTemplateColumns: `repeat(${maxCols}, 1fr)`,
    },
  }),
  
  // Responsive columns based on breakpoints
  responsiveColumns: {
    xs: 1,
    sm: 2,
    md: 2,
    lg: 3,
    xl: 4,
    xxl: 4,
  },
  
  // Dashboard layout (sidebar + content)
  dashboardLayout: (sidebarWidth: string = '280px') => ({
    display: 'grid',
    gridTemplateColumns: `${sidebarWidth} 1fr`,
    gap: 'clamp(1.5rem, 2vw, 3rem)',
    
    '@media (max-width: 1023px)': {
      gridTemplateColumns: '1fr',
    },
  }),
};


export const typographyScale = {
  display: responsiveFontSize(48, 96, { maxViewport: 1920 }), // 3rem -> 6rem
  h1: responsiveFontSize(40, 72, { maxViewport: 1920 }), // 2.5rem -> 4.5rem
  h2: responsiveFontSize(32, 56, { maxViewport: 1920 }), // 2rem -> 3.5rem
  h3: responsiveFontSize(24, 40, { maxViewport: 1920 }), // 1.5rem -> 2.5rem
  h4: responsiveFontSize(20, 32, { maxViewport: 1920 }), // 1.25rem -> 2rem
  body: responsiveFontSize(14, 18, { capAtReadableSize: true }), // Cap at 18px for readability
  small: responsiveFontSize(12, 14),
};

/**
 * Spacing scale for consistent layouts across all screen sizes
 */
export const spacingScale = {
  xs: fluidSpacing(4, 8),
  sm: fluidSpacing(8, 12),
  md: fluidSpacing(16, 24),
  lg: fluidSpacing(24, 40),
  xl: fluidSpacing(32, 56),
  xxl: fluidSpacing(48, 80),
  xxxl: fluidSpacing(64, 128),
};

/**
 * Utility to check if viewport is considered "large"
 */
export const isLargeViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 1280;
};

/**
 * Utility to check if viewport is ultra-wide
 */
export const isUltraWideViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 1920;
};

/**
 * Get optimal column count based on viewport width
 */
export const getOptimalColumnCount = (
  itemWidth: number = 300,
  maxColumns: number = 4
): number => {
  if (typeof window === 'undefined') return 1;

  const viewportWidth = window.innerWidth;
  const padding = viewportWidth < 640 ? 48 : viewportWidth < 1024 ? 64 : 80;
  const availableWidth = Math.min(viewportWidth - padding, 1920);

  const calculatedColumns = Math.floor(availableWidth / itemWidth);
  return Math.min(Math.max(calculatedColumns, 1), maxColumns);
};

export const getFormFieldWidth = (): string => {
  if (typeof window === 'undefined') return '456px';

  const width = window.innerWidth;

  // Mobile
  if (width < 640) return '100%';

  // Tablet
  if (width < 1024) return '100%';
  if (width <= 1440) {
    // Formula: currentWidth = minWidth + (maxWidth - minWidth) * (currentViewport - minViewport) / (maxViewport - minViewport)
    const minWidth = 247.33;
    const maxWidth = 455.33;
    const minViewport = 1024;
    const maxViewport = 1440;

    const calculatedWidth = minWidth + ((maxWidth - minWidth) * (width - minViewport) / (maxViewport - minViewport));
    return `${calculatedWidth.toFixed(2)}px`;
  }

  // Large desktop (1600px+) - slightly larger
  if (width >= 1600) return '480px';

  // Standard desktop (1300-1600px)
  return '456px';
};

/**
 * Get responsive form field width for fields inside cards
 * Same as getFormFieldWidth but 12.65px narrower
 */
export const getFormFieldWidthInCard = (): string => {
  if (typeof window === 'undefined') return '443.35px'; // 456 - 12.65

  const width = window.innerWidth;

  // Mobile
  if (width < 640) return '100%';

  // Tablet
  if (width < 1024) return '100%';

  // Linear interpolation from 234.68px at 1024px to 442.68px at 1440px (12.65px less)
  if (width <= 1440) {
    const minWidth = 234.68; // 247.33 - 12.65
    const maxWidth = 442.68; // 455.33 - 12.65
    const minViewport = 1024;
    const maxViewport = 1440;

    const calculatedWidth = minWidth + ((maxWidth - minWidth) * (width - minViewport) / (maxViewport - minViewport));
    return `${calculatedWidth.toFixed(2)}px`;
  }

  // Large desktop (1600px+) - slightly larger
  if (width >= 1600) return '467.35px'; // 480 - 12.65

  // Standard desktop (1300-1600px)
  return '443.35px'; // 456 - 12.65
};

/**
 * Get responsive outer padding for create/edit pages
 * Used in CreateShipmentPage, EditShipmentPage, CreateItineraryPage, EditItineraryPage
 * Consolidates duplicate getOuterPadding functions
 */
export const getPageOuterPadding = (isMobile: boolean, isTablet: boolean): string => {
  if (isMobile) return '24px 16px 80px';
  if (isTablet) return '32px 24px 80px';
  return '40px 120px 80px';
};

/**
 * Get responsive inner padding for create/edit pages
 * Used in CreateShipmentPage, EditShipmentPage, CreateItineraryPage, EditItineraryPage
 * Consolidates duplicate getInnerPadding functions
 */
export const getPageInnerPadding = (isMobile: boolean, isTablet: boolean): string => {
  if (isMobile) return '24px 16px';
  if (isTablet) return '32px 24px';
  return '40px 80px';
};

/**
 * Get responsive container padding for create/edit pages
 * Used in CreateShipmentPage, EditShipmentPage, CreateItineraryPage, EditItineraryPage
 * Consolidates duplicate getContainerPadding functions
 */
export const getPageContainerPadding = (isMobile: boolean): string => {
  if (isMobile) return '0px';
  return '0px 48px';
};

/**
 * Chat (group/delivery chat) layout constraints, shared by GroupChatRoom and MessageInput
 * so the whole chat widget scales consistently from small phones up through 4K/ultra-wide,
 * instead of freezing at its largest declared breakpoint.
 */
export const chatLayout = {
  // Outer widget max-width so the chat card doesn't stretch edge-to-edge on very wide screens,
  // while still using meaningfully more of the viewport as it grows (was capped too narrow,
  // leaving large empty margins on laptop/desktop widths).
  containerMaxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '1100px', xl: '1280px', xxl: '1440px' },
  cardPadding: { xs: '10px', sm: '16px', md: '24px', lg: '32px', xl: '44px', xxl: '52px' },
  headerHeight: { xs: 'auto', sm: '42px', md: '46px', lg: '52px', xl: '60px', xxl: '66px' },
  headerAvatarSize: { xs: 22, sm: 26, md: 28, lg: 32, xl: 38, xxl: 42 },
  headerAvatarOverlap: { xs: -5, sm: -6, md: -7, lg: -8, xl: -10, xxl: -11 },
  messageAreaPadding: { xs: '8px', sm: '10px', md: '12px', lg: '16px', xl: '20px', xxl: '24px' },
  messageAreaGap: { xs: '6px', sm: '10px', md: '14px', lg: '20px', xl: '24px', xxl: '26px' },
  // Viewport-relative height (mirrors ChatInbox's 1:1 chat) so the message list fills the
  // screen predictably instead of reserving a fixed band that's empty on short conversations.
  // The subtracted offset shrinks on small screens (where chrome takes proportionally less
  // vertical space) and grows on large screens (more chrome: bigger header/padding/input).
  messageAreaHeight: {
    xs: 'calc(100vh - 300px)',
    sm: 'calc(100vh - 320px)',
    md: 'calc(100vh - 340px)',
    lg: 'calc(100vh - 360px)',
    xl: 'calc(100vh - 380px)',
    xxl: 'calc(100vh - 400px)',
  },
  messageAreaMinHeight: { xs: '220px', sm: '260px', md: '300px', lg: '340px', xl: '380px', xxl: '420px' },
  messageBubbleMaxWidth: { xs: '75%', sm: '75%', md: '65%', lg: '58%', xl: '56%', xxl: '52%' },
  messageAvatarSize: { xs: 26, sm: 26, md: 28, lg: 32, xl: 36, xxl: 40 },
  inputAreaPadding: { xs: '8px', sm: '10px', md: '11px', lg: '14px', xl: '18px', xxl: '20px' },
} as const;

/**
 * Shared sizing for detail popups (SenderDetailsDialog, UserDetailCard, ...).
 * xs = phones, sm+ = tablet/desktop. Use these in `sx` so every popup scales the same way.
 */
export const dialogLayout = {
  paper: {
    width: { xs: 'calc(100% - 32px)', sm: '512px' },
    maxWidth: '512px',
    margin: { xs: '16px', sm: '32px' },
    maxHeight: { xs: '90vh', sm: '85vh' },
    borderRadius: '12px',
    boxShadow:
      '0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
  },
  maxHeight: { xs: '90vh', sm: '85vh' },

  headerPadding: { xs: '12px 16px 0px', sm: '24px 24px 0px' },
  headerGap: { xs: '10px', sm: '16px' },
  headerSpacerHeight: { xs: '12px', sm: '20px' },
  titleFontSize: { xs: '17px', sm: '24px' },
  titleLineHeight: { xs: '25px', sm: '36px' },
  closeButtonSize: { xs: '36px', sm: '44px' },
  closeButtonOffset: { xs: '8px', sm: '16px' },

  profilePadding: { xs: '0px 16px 12px', sm: '0px 24px 20px' },
  avatarSize: 48,
  avatarSizeSmall: 40,
  nameFontSize: { xs: '16px', sm: '18px' },
  nameLineHeight: { xs: '24px', sm: '27px' },

  bodyPadding: { xs: '0px 16px', sm: '0px 24px' },
  bodyGap: { xs: '14px', sm: '20px' },
  innerGap: { xs: '6px', sm: '12px' },

  routeIconSize: { xs: '20px', sm: '24px' },
  routeIconInnerSize: { xs: '10px', sm: '12px' },
  locationFontSize: { xs: '12px', sm: '16px' },
  locationLineHeight: { xs: '16px', sm: '24px' },
  dateFontSize: { xs: '12px', sm: '13px' },
  dateLineHeight: { xs: '18px', sm: '20px' },
  routeDashWidth: { xs: '16px', sm: '40px', md: '82px' },
  planeIconSize: { xs: '14px', sm: '20px' },
  planeMarginTop: { xs: '18px', sm: '22px' },

  detailsGap: { xs: '10px', sm: '13px' },
  detailIconSize: { xs: '28px', sm: '40px' },
  detailIconInnerSize: { xs: '15px', sm: '24px' },
  detailIconInnerSizeSmall: { xs: '14px', sm: '20px' },
  labelFontSize: { xs: '12px', sm: '14px' },
  labelLineHeight: { xs: '16px', sm: '21px' },
  valueFontSize: { xs: '13px', sm: '16px' },
  valueLineHeight: { xs: '18px', sm: '24px' },

  footerPadding: { xs: '12px 16px 16px', sm: '16px 24px 24px' },
  buttonHeight: { xs: '32px', sm: '44px' },
  buttonPadding: { xs: '6px 16px', sm: '12px 24px' },
  buttonFontSize: { xs: '12px', sm: '14px' },
  buttonLineHeight: { xs: '16px', sm: '21px' },
} as const;

/**
 * Shared sizing for listing cards (My Items ItemCard, sender/traveler OrderCard, ...).
 * One card per row on phones and tablets, two per row from desktop (lg) up.
 */
export const listCardLayout = {
  width: { xs: '100%', lg: 'calc(50% - 12px)' },
  listGap: { xs: '16px', sm: '20px', lg: '24px' },
  padding: { xs: '12px', sm: '16px' },
  sectionGap: { xs: '12px', sm: '16px' },
  rowGap: { xs: '10px', sm: '12px' },

  avatarSize: 38,
  avatarSizeSmall: 34,
  // lg steps back down from sm's peak since two cards now share the row, not one.
  nameFontSize: { xs: '14px', sm: '16px', lg: '15px' },
  nameLineHeight: { xs: '20px', sm: '24px', lg: '22px' },
  metaFontSize: { xs: '12px', sm: '14px', lg: '13px' },
  metaLineHeight: { xs: '18px', sm: '21px', lg: '19px' },

  labelFontSize: { xs: '12px', sm: '14px', lg: '13px' },
  labelLineHeight: { xs: '18px', sm: '21px', lg: '19px' },
  valueFontSize: { xs: '13px', sm: '16px', lg: '14px' },
  valueLineHeight: { xs: '19px', sm: '24px', lg: '21px' },

  routeIconSize: { xs: '19px', sm: '22px', lg: '20px' },
  routeIconInnerSize: { xs: '10px', sm: '12px', lg: '11px' },
  locationFontSize: { xs: '13px', sm: '15px', lg: '14px' },
  locationLineHeight: { xs: '19px', sm: '22px', lg: '20px' },

  buttonGap: { xs: '8px', sm: '12px' },
  buttonHeight: { xs: '36px', sm: '41px', lg: '38px' },
  buttonPadding: { xs: '8px 12px', sm: '10px 20px', lg: '8px 16px' },
  buttonFontSize: { xs: '13px', sm: '14px', lg: '13px' },
  buttonLineHeight: { xs: '19px', sm: '21px', lg: '19px' },
  // A row of side-by-side buttons that stacks when their labels no longer fit on one line
  buttonRow: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%' },
  buttonRowItem: { flex: '1 1 0px', whiteSpace: 'nowrap' },
  // Same, but always stays one row (e.g. Received Offers / Pending Requests); pair with
  // fixedButtonLabel on the text so it ellipsizes instead of the pair dropping to two rows.
  fixedButtonRow: { display: 'flex', flexDirection: 'row', width: '100%' },
  fixedButtonRowItem: { flex: '1 1 0px', minWidth: 0 },
  fixedButtonLabel: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: 0 },

  // Whole card navigates; stop propagation on inner buttons/links so they still work.
  clickableCard: {
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-2px)',
    },
  },

  // Explicit "View Details" button, desktop only (clickableCard covers phone/tablet).
  viewDetailsButtonWrap: { display: { xs: 'none', lg: 'block' }, width: '100%' },
} as const;

/**
 * Shared sizing for detail pages (Item/Order/Itinerary details for both sender and traveler).
 * These pages are built from repeated "info card" sections, each a title plus icon rows of
 * label/value pairs. Use these tokens (or the shared DetailInfoCard/DetailInfoRow components
 * in shared/components/cards) instead of hardcoding sizes per page.
 */
export const detailPageLayout = {
  pageTitleFontSize: { xs: '15px', sm: '16px', md: '18px' },
  pageTitleLineHeight: { xs: '22px', sm: '24px', md: '28px' },

  cardPadding: { xs: '16px', lg: '14px' },
  cardGap: '16px',
  titleFontSize: { xs: '15px', sm: '16px', md: '18px', lg: '16px' },
  titleLineHeight: { xs: '22px', sm: '24px', md: '27px', lg: '24px' },
  rowGap: { xs: '10px', sm: '12px' },
  iconSize: { xs: '32px', sm: '36px', md: '40px', lg: '36px' },
  iconInnerSize: { xs: '16px', sm: '18px', md: '20px', lg: '18px' },
  labelFontSize: { xs: '12px', sm: '13px', md: '14px', lg: '13px' },
  labelLineHeight: { xs: '18px', sm: '19px', md: '21px', lg: '19px' },
  valueFontSize: { xs: '12px', sm: '13px', md: '16px', lg: '14px' },
  valueLineHeight: { xs: '18px', sm: '19px', md: '24px', lg: '21px' },
  // Value column never grows past this, so a long name/email wraps instead of squeezing the label
  valueMaxWidth: { xs: '55%', sm: '60%' },

  // Step back down at lg: two-column layout narrows each card, so the md peak size overflows.
  actionButtonHeight: { xs: '38px', sm: '41px', md: '44px', lg: '36px' },
  actionButtonPadding: { xs: '8px 14px', sm: '10px 20px', lg: '6px 10px' },
  actionButtonFontSize: { xs: '12px', sm: '13px', md: '14px', lg: '11px' },
  actionButtonLineHeight: { xs: '18px', sm: '19px', md: '21px', lg: '16px' },
  // Page-level action button row (Delete/Edit, Cancel/Accept, ...); stacks full-width on phones.
  actionButtonRow: { display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: '100%' },
  actionButtonRowItem: { flex: { xs: '1 1 auto', sm: '1 1 0px' }, width: '100%', minWidth: 0 },

  // Left/right card-group layout; single column through tablet, splits in two on desktop.
  // alignItems stretch (not flex-start) so each column fills the row's width below lg, where
  // flexDirection is 'column' and the columns would otherwise shrink to their content's width.
  twoColumnRow: { display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'stretch', lg: 'flex-start' }, width: '100%' },
  twoColumn: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, minWidth: 0, width: '100%' },
} as const;

/**
 * Outer/inner padding for the 4 detail pages (Item/Order/Itinerary details). Mobile uses a
 * single 16px inset instead of stacking getPageOuterPadding + getPageInnerPadding's 16px each,
 * which left the inner cards 32px (64px combined) narrower than the screen on phones.
 */
export const getDetailPageOuterPadding = (isMobile: boolean, isTablet: boolean): string => {
  if (isMobile) return '16px 16px 80px';
  return getPageOuterPadding(isMobile, isTablet);
};

export const getDetailPageInnerPadding = (isMobile: boolean, isTablet: boolean): string => {
  if (isMobile) return '16px 0px';
  return getPageInnerPadding(isMobile, isTablet);
};

/**
 * Fluid message-input sizing keyed off screenSize (from useResponsive), so the input/button
 * grow smoothly across all breakpoints instead of only toggling between a mobile/desktop pair.
 */
export const getMessageInputSizing = (
  screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'ultrawide',
  compact: boolean
) => {
  const isSmall = screenSize === 'xs' || screenSize === 'sm';
  const isXLarge = screenSize === 'xxl' || screenSize === 'ultrawide';
  const isLarge = screenSize === 'xl' || isXLarge;

  const fieldFontSize = compact
    ? isSmall ? '12px' : isXLarge ? '16px' : isLarge ? '14px' : '13px'
    : isSmall ? '14px' : isXLarge ? '19px' : isLarge ? '17px' : '16px';

  const fieldLineHeight = compact
    ? isSmall ? '17px' : isXLarge ? '21px' : isLarge ? '19px' : '18px'
    : isSmall ? '21px' : isXLarge ? '27px' : isLarge ? '25px' : '24px';

  const fieldHeight = compact
    ? isSmall ? 'auto' : isXLarge ? '48px' : isLarge ? '42px' : '38px'
    : isSmall ? 'auto' : isXLarge ? '66px' : isLarge ? '60px' : '56px';

  const fieldPaddingRight = compact
    ? isSmall ? '70px' : isXLarge ? '100px' : isLarge ? '92px' : '84px'
    : isSmall ? '90px' : isXLarge ? '126px' : isLarge ? '118px' : '110px';

  const actionsRight = compact
    ? isSmall ? '8px' : isXLarge ? '14px' : isLarge ? '12px' : '10px'
    : isSmall ? '12px' : isXLarge ? '20px' : isLarge ? '18px' : '16px';

  const buttonHeight = compact
    ? isSmall ? '28px' : isXLarge ? '36px' : isLarge ? '32px' : '30px'
    : isSmall ? '36px' : isXLarge ? '48px' : isLarge ? '44px' : '41px';

  const buttonMinWidth = compact
    ? isSmall ? '48px' : isXLarge ? '68px' : isLarge ? '60px' : '56px'
    : isSmall ? '60px' : isXLarge ? '88px' : isLarge ? '80px' : '76px';

  const buttonFontSize = compact
    ? isXLarge ? '13px' : '11px'
    : isSmall ? '12px' : isXLarge ? '16px' : isLarge ? '15px' : '14px';

  return {
    fieldFontSize,
    fieldLineHeight,
    fieldHeight,
    fieldPaddingRight,
    actionsRight,
    buttonHeight,
    buttonMinWidth,
    buttonFontSize,
  };
};

export default {
  useResponsive,
  useCustomMediaQuery,
  getResponsiveValue,
  fluidSpacing,
  responsiveFontSize,
  containerQuery,
  aspectRatio,
  responsivePadding,
  gridSpacing,
  responsiveBorderRadius,
  maxWidthConstraints,
  viewportUtils,
  columnLayouts,
  typographyScale,
  spacingScale,
  isLargeViewport,
  isUltraWideViewport,
  getOptimalColumnCount,
  getFormFieldWidth,
  getFormFieldWidthInCard,
  getPageOuterPadding,
  getPageInnerPadding,
  getPageContainerPadding,
  getDetailPageOuterPadding,
  getDetailPageInnerPadding,
  chatLayout,
  dialogLayout,
  listCardLayout,
  detailPageLayout,
  getMessageInputSizing,
};